import { ConfidenceLevel } from "@/data/models/AiPrediction";
import {
  COLOR_TRACK_DRY,
  COLOR_TRACK_STATIONARY,
  COLOR_TRACK_MOISTURE,
  COLOR_TRACK_SLOW_DRYING,
  COLOR_TRACK_POWER_OUTAGE,
  COLOR_TRACK_RELAY_DEFECT,
  COLOR_TRACK_AMBIENT,
  COLOR_TRACK_AIRFLOW,
  COLOR_TRACK_PERIPHERAL_MOISTURE,
  COLOR_TRACK_ROOM_TEMPERATURE,
  COLOR_TRACK_ROOM_HUMIDITY,
  COLOR_TRACK_MOISTURE_LOAD,
  COLOR_TRACK_DEVICE_OUTPUT,
  COLOR_TRACK_FAVORABLE_WEATHER,
} from "@/utils/colors";

/**
 * Styling for the RENO time-series event tracks (RenoTimeSeries.vue).
 *
 * Two independent visual channels keep the tracks readable:
 * - **Color = domain identity** — each track's own meaning-based color, echoing
 *   the main drying graph above where relevant (`getTrackColor`).
 * - **Line weight + opacity = model confidence** — sure predictions are bold and
 *   opaque, unsure ones thinner and faded (`getModelConfidenceStyle`).
 *
 * Keeping the channels orthogonal avoids the previous black↔yellow flicker,
 * where a single stroke color tried to encode confidence alone.
 */

/** Domain color per track, keyed by its target key (see `LABEL_GROUPS` in RenoTimeSeriesLabels.ts). */
const TRACK_COLORS: Record<string, string> = {
  dry: COLOR_TRACK_DRY,
  stationary: COLOR_TRACK_STATIONARY,
  newMoistureIngress: COLOR_TRACK_MOISTURE,
  slowDrying: COLOR_TRACK_SLOW_DRYING,
  powerOutage: COLOR_TRACK_POWER_OUTAGE,
  relayDefect: COLOR_TRACK_RELAY_DEFECT,
  unfavorableAmbient: COLOR_TRACK_AMBIENT,
  lowAirflow: COLOR_TRACK_AIRFLOW,
  peripheralMoisture: COLOR_TRACK_PERIPHERAL_MOISTURE,
  roomTemperature: COLOR_TRACK_ROOM_TEMPERATURE,
  roomHumidity: COLOR_TRACK_ROOM_HUMIDITY,
  moistureLoad: COLOR_TRACK_MOISTURE_LOAD,
  deviceOutput: COLOR_TRACK_DEVICE_OUTPUT,
  favorableWeather: COLOR_TRACK_FAVORABLE_WEATHER,
};

/**
 * Domain stroke color for a track. Unknown keys fall back to the neutral
 * stationary grey rather than an arbitrary hue.
 */
export function getTrackColor(trackKey: string): string {
  return TRACK_COLORS[trackKey] ?? COLOR_TRACK_STATIONARY;
}

/** SVG stroke properties expressing model confidence (color applied separately). */
export interface ConfidenceStyle {
  strokeWidth: number;
  strokeOpacity: number;
}

/**
 * Line style for a model-confidence level. Confidence is collapsed to three
 * visible states (`low` and `medium` are treated identically for now):
 * - `high` → bold, fully opaque.
 * - `low` / `medium` → thinner and faded.
 * - `none` → `null`, i.e. draw nothing (no prediction for that interval).
 */
export function getModelConfidenceStyle(level: ConfidenceLevel): ConfidenceStyle | null {
  switch (level) {
    case "high":
      return { strokeWidth: 3, strokeOpacity: 1 };
    case "medium":
    case "low":
      return { strokeWidth: 2, strokeOpacity: 0.55 };
    case "none":
      return null;
    default: {
      // Exhaustiveness guard — a new ConfidenceLevel member becomes a compile error here.
      const _exhaustive: never = level;
      return _exhaustive;
    }
  }
}
